import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import ThemeToggle from './ThemeToggle';

const navLinks = [
  { name: 'ABOUT', href: '#about' },
  { name: 'EXPERIENCE', href: '#experience' },
  { name: 'PROJECTS', href: '#projects' },
  { name: 'SKILLS', href: '#skills' },
  { name: 'CONTACT', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isCyberpunk } = useTheme();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const getHref = (hash) => isHome ? hash : `/${hash}`;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
        scrolled
          ? isCyberpunk
            ? 'bg-cyber-black/90 backdrop-blur-md border-cyber-magenta/20'
            : 'bg-white/90 backdrop-blur-md border-gray-200 shadow-sm'
          : 'bg-transparent border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <motion.a
          href={getHref('#hero')}
          className={`font-display text-xl tracking-widest relative group transition-colors duration-500 ${
            isCyberpunk ? 'text-white' : 'text-gray-900'
          }`}
          whileHover={{ scale: 1.05 }}
        >
          <span className={isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-600'}>&lt;</span>
          SB
          <span className={isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-600'}>/&gt;</span>
          <span className={`absolute -bottom-1 left-0 w-0 h-[1px] group-hover:w-full transition-all duration-300 ${
            isCyberpunk ? 'bg-cyber-magenta' : 'bg-indigo-600'
          }`} />
        </motion.a>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link, i) => (
            <motion.a
              key={link.name}
              href={getHref(link.href)}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i, duration: 0.5 }}
              className={`font-mono text-sm tracking-wider transition-colors duration-500 relative group ${
                isCyberpunk
                  ? 'text-cyber-dim hover:text-cyber-magenta'
                  : 'text-gray-500 hover:text-indigo-600'
              }`}
            >
              <span className={`mr-1 text-xs transition-colors duration-500 ${
                isCyberpunk ? 'text-cyber-magenta/60' : 'text-indigo-400/60'
              }`}>
                0{i + 1}.
              </span>
              {link.name}
              <span className={`absolute -bottom-1 left-0 w-0 h-[1px] group-hover:w-full transition-all duration-300 ${
                isCyberpunk ? 'bg-cyber-magenta' : 'bg-indigo-600'
              }`} />
            </motion.a>
          ))}

          {/* Theme Toggle */}
          <ThemeToggle />

          <motion.a
            href="/ResumeBuckler.pdf"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className={`font-mono text-sm px-4 py-2 tracking-wider border transition-all duration-500 ${
              isCyberpunk
                ? 'border-cyber-magenta/50 text-cyber-magenta hover:bg-cyber-magenta/10'
                : 'border-indigo-600/50 text-indigo-600 hover:bg-indigo-50'
            }`}
          >
            RESUME
          </motion.a>
        </div>

        {/* Mobile: toggle + menu */}
        <div className="md:hidden flex items-center gap-4">
          <ThemeToggle />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-600'}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden backdrop-blur-lg border-b transition-colors duration-500 ${
              isCyberpunk
                ? 'bg-cyber-dark/95 border-cyber-magenta/20'
                : 'bg-white/95 border-gray-200'
            }`}
          >
            <div className="flex flex-col items-center gap-6 py-8">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.name}
                  href={getHref(link.href)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => setMobileOpen(false)}
                  className={`font-mono text-sm tracking-wider transition-colors ${
                    isCyberpunk
                      ? 'text-cyber-dim hover:text-cyber-magenta'
                      : 'text-gray-500 hover:text-indigo-600'
                  }`}
                >
                  <span className={isCyberpunk ? 'text-cyber-magenta/60 mr-2' : 'text-indigo-400/60 mr-2'}>
                    0{i + 1}.
                  </span>
                  {link.name}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
